// src/platform/dashboards/AiDashboardCard.jsx
//
// One card in the AI Dashboards grid.
//
// The card is driven entirely by the API's `rollup` — a headline, a short list
// of stats, and a two-segment progress bar. It renders whatever it is given
// rather than knowing anything about wellheads, so a second dashboard gets a
// working card from its rollup alone.
//
// It is a <button>, not a clickable <div>: the whole card is the target, and
// keyboard users get it for free.

import React from "react";
import { ArrowRight, AlertCircle } from "lucide-react";
import { TONE, fmt, pct } from "./format";

export default function AiDashboardCard({ card, onOpen }) {
  const { title, subtitle, product, rollup, available } = card;

  if (!available) {
    return (
      <div className="rounded-lg border border-bg-border bg-bg-soft p-4 opacity-70">
        <div className="flex items-center gap-2">
          <AlertCircle size={14} className="text-amber-400 shrink-0" />
          <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
        </div>
        <p className="text-[11px] text-fg-secondary mt-1.5">{subtitle}</p>
        <p className="text-[11px] text-amber-300/80 mt-3">
          {card.error || "Rollup unavailable"} — the dashboard cannot be opened right now.
        </p>
      </div>
    );
  }

  const progressTotal = rollup.progress?.reduce((s, p) => s + p.value, 0) || 0;

  return (
    <button
      type="button"
      onClick={() => onOpen(card.key)}
      className="group text-left rounded-lg border border-bg-border bg-bg-soft p-4
                 hover:border-accent-action/50 hover:bg-bg-softer
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-action/70
                 transition flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
          <p className="text-[11px] text-fg-secondary mt-0.5 leading-relaxed">{subtitle}</p>
        </div>
        <ArrowRight
          size={15}
          className="text-fg-secondary group-hover:text-accent-info shrink-0 mt-0.5
                     transition-transform group-hover:translate-x-0.5"
        />
      </div>

      {rollup.headline ? (
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-semibold tracking-tight tabular-nums text-fg-primary leading-none">
            {rollup.headline.value}
          </span>
          <span className="text-[11px] text-fg-secondary">{rollup.headline.label}</span>
        </div>
      ) : null}

      {progressTotal > 0 ? (
        <div className="flex h-1.5 gap-[2px] rounded overflow-hidden bg-bg-softer">
          {rollup.progress.filter((p) => p.value > 0).map((p) => (
            <div key={p.key}
                 className={(TONE[p.tone] ?? TONE.neutral).fill}
                 style={{ width: `${pct(p.value, progressTotal)}%` }} />
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-auto pt-1">
        {(rollup.stats ?? []).map((s) => (
          <div key={s.key} className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider text-fg-secondary">
              {s.label}
            </div>
            <div className={`text-sm font-medium tabular-nums ${
              s.tone === "neutral" || !TONE[s.tone]
                ? "text-fg-primary"
                : TONE[s.tone].text}`}>
              {fmt(s.value)}
            </div>
          </div>
        ))}
      </div>

      {product ? (
        <span className="self-start text-[10px] px-1.5 py-0.5 rounded border border-bg-border
                         text-fg-secondary tracking-wide">
          {product}
        </span>
      ) : null}
    </button>
  );
}
